// Analytics utility for Clock-It
// Tracks user interactions and app usage patterns

export interface AnalyticsEvent {
  event: string;
  properties?: Record<string, any>;
  timestamp: number;
  userId?: string;
  sessionId: string;
}

export interface AnalyticsData {
  totalSessions: number;
  totalEvents: number;
  dailyActiveUsers: number;
  averageSessionDuration: number;
  mostUsedFeatures: Array<{ feature: string; count: number }>;
  recentEvents: AnalyticsEvent[];
}

class AnalyticsManager {
  private sessionId: string;
  private sessionStart: number;
  private events: AnalyticsEvent[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStart = Date.now();
    this.loadStoredEvents();
    
    // Track session start
    this.track('session_start');
    
    // Track session end on page unload
    window.addEventListener('beforeunload', () => {
      this.track('session_end', {
        session_duration: Date.now() - this.sessionStart
      });
    });
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private loadStoredEvents(): void {
    try {
      const stored = localStorage.getItem('clockit-analytics-events');
      if (stored) {
        this.events = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load analytics events:', error);
      this.events = [];
    }
  }

  private saveEvents(): void {
    try {
      // Keep only last 1000 events to prevent localStorage bloat
      const eventsToStore = this.events.slice(-1000);
      localStorage.setItem('clockit-analytics-events', JSON.stringify(eventsToStore));
    } catch (error) {
      console.warn('Failed to save analytics events:', error);
    }
  }

  track(eventName: string, properties?: Record<string, any>): void {
    const event: AnalyticsEvent = {
      event: eventName,
      properties,
      timestamp: Date.now(),
      sessionId: this.sessionId
    };

    this.events.push(event);
    this.saveEvents();
    
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics:', eventName, properties);
    }
  }

  getAnalyticsData(): AnalyticsData {
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    const oneWeekAgo = now - (7 * 24 * 60 * 60 * 1000);

    // Get unique sessions in the last week
    const recentSessions = new Set(
      this.events
        .filter(event => event.timestamp > oneWeekAgo)
        .map(event => event.sessionId)
    );

    // Calculate daily active users (unique sessions in last 24h)
    const dailyActiveSessions = new Set(
      this.events
        .filter(event => event.timestamp > oneDayAgo)
        .map(event => event.sessionId)
    );

    // Calculate average session duration
    const sessionDurations = this.getSessionDurations();
    const averageSessionDuration = sessionDurations.length > 0 
      ? sessionDurations.reduce((sum, duration) => sum + duration, 0) / sessionDurations.length
      : 0;

    // Get most used features
    const featureUsage = new Map<string, number>();
    this.events
      .filter(event => event.timestamp > oneWeekAgo)
      .forEach(event => {
        const current = featureUsage.get(event.event) || 0;
        featureUsage.set(event.event, current + 1);
      });

    const mostUsedFeatures = Array.from(featureUsage.entries())
      .map(([feature, count]) => ({ feature, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Get recent events (last 50)
    const recentEvents = this.events.slice(-50);

    return {
      totalSessions: recentSessions.size,
      totalEvents: this.events.filter(event => event.timestamp > oneWeekAgo).length,
      dailyActiveUsers: dailyActiveSessions.size,
      averageSessionDuration: Math.round(averageSessionDuration / 1000), // Convert to seconds
      mostUsedFeatures,
      recentEvents
    };
  }

  private getSessionDurations(): number[] {
    const sessionDurations: number[] = [];
    const sessionStarts = new Map<string, number>();
    const sessionEnds = new Map<string, number>();

    this.events.forEach(event => {
      if (event.event === 'session_start') {
        sessionStarts.set(event.sessionId, event.timestamp);
      } else if (event.event === 'session_end') {
        sessionEnds.set(event.sessionId, event.timestamp);
      }
    });

    sessionStarts.forEach((startTime, sessionId) => {
      const endTime = sessionEnds.get(sessionId);
      if (endTime) {
        sessionDurations.push(endTime - startTime);
      }
    });

    return sessionDurations;
  }

  // Convenience methods for common events
  trackScreenView(screenName: string): void {
    this.track('screen_view', { screen: screenName });
  }

  trackClockIn(): void {
    this.track('clock_in');
  }

  trackClockOut(): void {
    this.track('clock_out');
  }

  trackFeatureUsage(featureName: string, properties?: Record<string, any>): void {
    this.track('feature_usage', { feature: featureName, ...properties });
  }

  trackRightsAccess(category: string): void {
    this.track('rights_access', { category });
  }

  trackScheduleImport(method: 'manual' | 'photo' | 'file', count: number): void {
    this.track('schedule_import', { method, count });
  }

  // Clear all analytics data (for privacy)
  clearData(): void {
    this.events = [];
    localStorage.removeItem('clockit-analytics-events');
  }
}

export const analytics = new AnalyticsManager();

// React hook for analytics
export function useAnalytics() {
  return {
    track: analytics.track.bind(analytics),
    trackScreenView: analytics.trackScreenView.bind(analytics),
    trackClockIn: analytics.trackClockIn.bind(analytics),
    trackClockOut: analytics.trackClockOut.bind(analytics),
    trackFeatureUsage: analytics.trackFeatureUsage.bind(analytics),
    trackRightsAccess: analytics.trackRightsAccess.bind(analytics),
    trackScheduleImport: analytics.trackScheduleImport.bind(analytics),
    getAnalyticsData: analytics.getAnalyticsData.bind(analytics),
    clearData: analytics.clearData.bind(analytics)
  };
}